import {
  ThemeProviderContext,
  ThemeProviderState,
} from '@mxa-argos/components/refine/theme/theme-context';
import { useEffect, useMemo, useState } from 'react';

type ThemeProviderProps = {
  children: React.ReactNode;
};

function getResolvedTheme(): 'dark' | 'light' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  const [resolvedTheme, setResolvedTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window === 'undefined') return 'light';
    return getResolvedTheme();
  });

  useEffect(() => {
    const root = window.document.documentElement;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const applyTheme = () => {
      const resolved = mediaQuery.matches ? 'dark' : 'light';
      root.classList.remove('light', 'dark');
      root.classList.add(resolved);
      setResolvedTheme(resolved);
    };

    applyTheme();
    mediaQuery.addEventListener('change', applyTheme);
    return () => mediaQuery.removeEventListener('change', applyTheme);
  }, []);

  const value: ThemeProviderState = useMemo(
    () => ({ theme: 'system' as const, resolvedTheme }),
    [resolvedTheme],
  );

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

ThemeProvider.displayName = 'ThemeProvider';
